/**
 * Catálogo de plantillas para Mensajes generales (correo a clientes).
 * CRUD + recurrencia sugerida. La generación automática por periodo es fase 2.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSectionCard, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  despachoMensajesGeneralesApi,
  parseMensajesGeneralesError,
} from "@/modules/despacho/despachoMensajesGeneralesApi";

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Sin repetición" },
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
  { value: "annual", label: "Anual" },
];

const MESSAGE_KIND_OPTIONS = [
  { value: "recordatorio", label: "Recordatorio" },
  { value: "envio_documentacion", label: "Envío de documentación" },
];

const emptyForm = {
  name: "",
  body: "",
  message_kind: "recordatorio",
  recurrence_mode: "monthly",
  recurrence_day_of_month: 5,
  recurrence_weekday: 0,
  recurrence_month: 1,
  active: true,
};

export default function CalendarioMensajesPlantillasPanel({ readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await despachoMensajesGeneralesApi.listTemplates({ q, activeOnly: false });
      setItems(data.items || []);
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      toast.error(parseMensajesGeneralesError(err).message);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId("");
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || "",
      body: row.body || "",
      message_kind: row.message_kind || "recordatorio",
      recurrence_mode: row.recurrence_mode || "monthly",
      recurrence_day_of_month: row.recurrence_day_of_month || 5,
      recurrence_weekday: row.recurrence_weekday ?? 0,
      recurrence_month: row.recurrence_month || 1,
      active: row.active !== false,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!form.name.trim()) {
      toast.error("Indique el nombre de la plantilla.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        body: form.body,
        message_kind: form.message_kind || "recordatorio",
        recurrence_mode: form.recurrence_mode,
        recurrence_day_of_month: Number(form.recurrence_day_of_month) || 5,
        recurrence_weekday: Number(form.recurrence_weekday) || 0,
        recurrence_month: Number(form.recurrence_month) || 1,
        active: Boolean(form.active),
      };
      if (editId) {
        await despachoMensajesGeneralesApi.updateTemplate(editId, payload);
        toast.success("Plantilla actualizada.");
      } else {
        await despachoMensajesGeneralesApi.createTemplate(payload);
        toast.success("Plantilla creada.");
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (row) => {
    if (readOnly) return;
    try {
      await despachoMensajesGeneralesApi.duplicateTemplate(row.id);
      toast.success("Plantilla duplicada.");
      await load();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const handleDelete = async (row) => {
    if (readOnly) return;
    if (!window.confirm(`¿Eliminar la plantilla «${row.name}»?`)) return;
    try {
      await despachoMensajesGeneralesApi.removeTemplate(row.id);
      toast.success("Plantilla eliminada.");
      await load();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Nombre",
        render: (r) => (
          <div>
            <div className="font-medium text-slate-800">{r.name}</div>
            {!r.active ? <div className="text-[10px] text-amber-700">Inactiva</div> : null}
          </div>
        ),
      },
      {
        key: "message_kind",
        label: "Tipo",
        render: (r) => (
          <span className="text-slate-700 text-xs">
            {r.message_kind_label
              || MESSAGE_KIND_OPTIONS.find((o) => o.value === r.message_kind)?.label
              || "Recordatorio"}
          </span>
        ),
      },
      {
        key: "recurrence",
        label: "Recurrencia",
        render: (r) => (
          <span className="text-slate-700">
            {r.recurrence_label || r.recurrence_mode}
            {r.recurrence_mode === "monthly" ? ` · día ${r.recurrence_day_of_month}` : ""}
            {r.recurrence_mode === "weekly" ? ` · weekday ${r.recurrence_weekday}` : ""}
            {r.recurrence_mode === "annual" ? ` · mes ${r.recurrence_month}` : ""}
          </span>
        ),
      },
      {
        key: "body",
        label: "Texto",
        render: (r) => (
          <span className="text-slate-600 line-clamp-2 max-w-xs">{r.body || "—"}</span>
        ),
      },
      {
        key: "actions",
        label: "Acciones",
        render: (r) =>
          readOnly ? null : (
            <div className="flex flex-wrap gap-1">
              <Button type="button" size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => openEdit(r)}>
                <Pencil className="h-3 w-3 mr-1" aria-hidden />
                Editar
              </Button>
              <Button type="button" size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => handleDuplicate(r)}>
                <Copy className="h-3 w-3 mr-1" aria-hidden />
                Duplicar
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 rounded-sm text-[11px] text-red-700" onClick={() => handleDelete(r)}>
                <Trash2 className="h-3 w-3" aria-hidden />
              </Button>
            </div>
          ),
      },
    ],
    [readOnly],
  );

  return (
    <div className="space-y-4" data-testid="calendario-mensajes-plantillas">
      <DespachoInfoCallout>
        Catálogo de textos reutilizables (papelería, estado de cuenta, etc.). En{" "}
        <strong>Mensajes generales</strong> elija una plantilla al crear la campaña del periodo; el auxiliar
        confirma empresas y envía. La generación automática por fecha queda para una siguiente fase.
      </DespachoInfoCallout>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="min-w-[12rem] flex-1">
          <Label className="text-xs">Buscar plantilla</Label>
          <Input
            className="mt-1"
            placeholder="Nombre o texto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="mensaje-plantilla-buscar"
          />
        </div>
        {!readOnly ? (
          <Button type="button" size="sm" className="rounded-sm bg-[#0F2D5C]" onClick={openCreate} data-testid="mensaje-plantilla-nueva">
            <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
            Nueva plantilla
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={load}>
          Actualizar
        </Button>
      </div>

      <DespachoSectionCard title={`Plantillas (${items.length})`}>
        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando…
          </div>
        ) : (
          <DespachoSimpleTable columns={columns} rows={items} emptyLabel="No hay plantillas." />
        )}
      </DespachoSectionCard>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de campaña</Label>
              <Select
                value={form.message_kind || "recordatorio"}
                onValueChange={(v) => setForm((f) => ({ ...f, message_kind: v }))}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="mensaje-plantilla-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESSAGE_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre</Label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="mensaje-plantilla-nombre"
              />
            </div>
            <div>
              <Label>Recurrencia sugerida</Label>
              <Select
                value={form.recurrence_mode}
                onValueChange={(v) => setForm((f) => ({ ...f, recurrence_mode: v }))}
              >
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 m-0">
                Sirve de guía al crear campañas. El envío sigue siendo por confirmación en el listado.
              </p>
            </div>
            {form.recurrence_mode === "monthly" || form.recurrence_mode === "biweekly" ? (
              <div>
                <Label>Día del mes (referencia)</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  className="mt-1 w-24"
                  value={form.recurrence_day_of_month}
                  onChange={(e) => setForm((f) => ({ ...f, recurrence_day_of_month: e.target.value }))}
                />
              </div>
            ) : null}
            <div>
              <Label>Texto del correo</Label>
              <SpellcheckTextarea
                className="mt-1 min-h-[120px]"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Activa (aparece en el selector de Nuevo mensaje)
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setFormOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" className="rounded-sm bg-[#0F2D5C]" disabled={busy} onClick={handleSave} data-testid="mensaje-plantilla-guardar">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
